import React from 'react';
import { Auth, Hub } from 'aws-amplify';
import { useSiteDetection } from 'shared/hooks/use-site-detection';
import { useLinkSsoIdToUser } from 'shared/hooks/query-hooks/use-link-sso-id-to-user';
import { pools } from './config';
import { configureAmplify } from './utils';

type HubListenerFn = Parameters<typeof Hub.listen>['1'];

const CheckAuthentication = ({
  children,
}: {
  children: (signedIn: boolean) => React.ReactNode;
}) => {
  const { site } = useSiteDetection();
  const linkSsoUser = useLinkSsoIdToUser();
  const [auth, setAuth] = React.useState({
    settled: false,
    authenticated: false,
  });

  const signIn = React.useCallback(() => {
    setAuth({
      settled: true,
      authenticated: true,
    });
  }, []);

  const signOut = React.useCallback(() => {
    setAuth({
      settled: true,
      authenticated: false,
    });
  }, []);

  React.useEffect(() => {
    const listener: HubListenerFn = async (args) => {
      switch (args.payload.event) {
        case 'signIn':
          signIn();
          break;
        case 'signOut':
          signOut();
          break;
        case 'customOAuthState':
          if (args.payload.data === 'signed-in-via-sso') {
            const user = await Auth.currentAuthenticatedUser();
            const previousEmail =
              sessionStorage.getItem('sso-sign-in-email') ?? user.attributes?.email ?? '';
            sessionStorage.removeItem('sso-sign-in-email');
            const awsIdentityProvider = user.signInUserSession;
            const idpEmail = awsIdentityProvider.idToken.payload.email;
            const hasEmailChanged =
              idpEmail && previousEmail && idpEmail.toLowerCase() !== previousEmail.toLowerCase();

            if (hasEmailChanged) {
              sessionStorage.setItem('is-email-mismatch', 'true');
              Auth.signOut();
              return;
            }
            if (previousEmail && awsIdentityProvider.idToken.payload['cognito:username']) {
              linkSsoUser({
                email: previousEmail,
                awsIdentityProviderToken: awsIdentityProvider.idToken.payload['cognito:username'],
              });
            }
          }
      }
    };
    Hub.listen('auth', listener);

    return () => {
      Hub.remove('auth', listener);
    };
  }, []);

  React.useEffect(() => {
    const doSignIn = async () => {
      for (const pool of [pools.ogp, pools.hq]) {
        configureAmplify(pool);
        try {
          await Auth.currentAuthenticatedUser();
          signIn();
          return;
        } catch {}
      }

      const pool = pools[site];
      if (pool) {
        configureAmplify(pool);
      }
      signOut();
    };

    doSignIn();
  }, [signIn, signOut, site]);

  if (!auth.settled) {
    return null;
  }

  return <>{children(auth.authenticated)}</>;
};

export { CheckAuthentication };
